import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DemonEye from "@/components/DemonEye";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Sunrise,
  Sun,
  Moon,
  TrendingUp,
  Target,
  Shield,
  Calculator,
  Layers,
  ScrollText,
  Send,
} from "lucide-react";
import { LOT_TABLE, fmtUSD } from "@/lib/calc";
import InstallPWA from "@/components/InstallPWA";
import FloatingTelegram from "@/components/FloatingTelegram";
import api from "@/lib/api";

const TELEGRAM_URL = "https://t.me/DEMONCLUB92";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

function Section({ id, children, className = "" }) {
  return (
    <motion.section
      id={id}
      variants={fade}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      className={`max-w-6xl mx-auto px-5 py-16 sm:py-24 ${className}`}
    >
      {children}
    </motion.section>
  );
}

function Eyebrow({ children }) {
  return <div className="text-[11px] tracking-[0.35em] text-violet-400 mb-3">{children}</div>;
}

export default function Landing() {
  const start = new Date("2026-10-01T00:00:00Z").getTime();
  const [left, setLeft] = useState("");
  const [telegramUrl, setTelegramUrl] = useState(TELEGRAM_URL);

  useEffect(() => {
    api
      .get("/settings")
      .then(({ data }) => {
        if (data.telegram_group) setTelegramUrl(data.telegram_group);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      const diff = start - Date.now();
      if (diff <= 0) return setLeft("TRWA");
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setLeft(`${d}d ${h}h ${m}m`);
    }, 1000);
    return () => clearInterval(t);
  }, [start]);

  return (
    <div className="demon-bg min-h-screen text-demon-silver overflow-x-hidden">
      <div className="grid-overlay fixed inset-0 pointer-events-none opacity-30" />
      <FloatingTelegram />

      {/* Nav */}
      <header className="sticky top-0 z-40 glass border-b border-violet-900/30">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <DemonEye size={34} className="animate-eye" />
            <div>
              <div className="font-display font-extrabold text-sm leading-none text-glow">THE MONEY CLUB</div>
              <div className="text-[8px] tracking-[0.3em] text-demon-muted mt-0.5">TRADING ACADEMY</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="#zasady" className="hidden sm:block text-sm text-demon-muted hover:text-white transition">
              Zasady
            </a>
            <div className="hidden sm:block">
              <InstallPWA />
            </div>
            <Link to="/login">
              <Button
                data-testid="landing-login-button"
                className="bg-gradient-to-r from-violet-700 to-violet-500 border-0 h-9 shadow-[0_0_18px_rgba(168,85,247,0.35)]"
              >
                Zaloguj się
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-5 pt-16 pb-12 sm:pt-24 text-center">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-violet-700/12 blur-[130px] -z-0" />
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
          <DemonEye size={110} className="animate-eye mx-auto mb-6" />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-glow"
        >
          THE MONEY CLUB
        </motion.h1>
        <div className="text-sm sm:text-base tracking-[0.4em] text-demon-muted mt-3">92 DAYS CHALLENGE</div>

        <div className="flex items-center justify-center gap-3 mt-8 font-mono">
          <span className="glow-card rounded-xl px-6 py-3 text-xl sm:text-2xl font-bold">
            $350 <span className="text-violet-400">→</span> <span className="gold-glow text-amber-400">$200,000</span>
          </span>
        </div>

        <p className="max-w-2xl mx-auto text-demon-muted mt-8 text-base sm:text-lg leading-relaxed">
          92 dni. Jeden plan. Maksymalnie trzy tury dziennie. Zero przypadkowości.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-4 mt-9">
          <Link to="/login">
            <Button
              data-testid="hero-start-button"
              className="h-13 px-8 py-6 text-base font-display font-semibold bg-gradient-to-r from-violet-700 to-violet-500 border-0 shadow-[0_0_28px_rgba(168,85,247,0.45)]"
            >
              ROZPOCZNIJ CHALLENGE <ArrowRight size={18} className="ml-1" />
            </Button>
          </Link>
          <a href="#zasady">
            <Button
              variant="outline"
              className="h-13 px-8 py-6 text-base font-display border-violet-500/40 bg-transparent text-white hover:bg-violet-600/10"
            >
              ZOBACZ ZASADY
            </Button>
          </a>
          <a href={telegramUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-telegram-button">
            <Button className="h-13 px-8 py-6 text-base font-display font-semibold bg-gradient-to-r from-sky-600 to-cyan-500 border-0 shadow-[0_0_24px_rgba(56,189,248,0.4)]">
              <Send size={18} className="mr-2" /> DOŁĄCZ NA TELEGRAM
            </Button>
          </a>
        </div>

        <div className="mt-8 text-xs text-demon-muted font-mono">
          Start: 01.10.2026 · Do startu pozostało: <span className="text-violet-300">{left}</span>
        </div>
      </section>

      {/* O challenge */}
      <Section id="o-challenge">
        <Eyebrow>01 · O CHALLENGE'U</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl font-bold mb-5">Prywatny panel uczestnika</h2>
        <p className="text-demon-muted max-w-3xl leading-relaxed text-base">
          THE MONEY CLUB — 92 DAYS CHALLENGE to zdyscyplinowany plan wzrostu kapitału oparty na procencie składanym.
          Startujesz z <span className="text-white font-mono">$350</span> i przez 92 dni (01.10 – 31.12.2026)
          realizujesz maksymalnie trzy tury dziennie, zapisując każdy wynik. Panel automatycznie przelicza kapitał,
          buduje historię i pokazuje, czy jesteś powyżej czy poniżej planu.
        </p>
        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          {[
            { icon: TrendingUp, t: "Procent składany", d: "Każda tura buduje się na poprzednim kapitale." },
            { icon: Layers, t: "Max 3 tury / dzień", d: "Rano · Po południu · Wieczorem." },
            { icon: Shield, t: "Zarządzanie ryzykiem", d: "RR 1:1, stały plan, zero odrabiania strat." },
          ].map((c) => (
            <div key={c.t} className="glow-card rounded-2xl p-6 neon-ring transition-all">
              <c.icon className="text-violet-400 mb-3" size={26} />
              <div className="font-display font-semibold text-lg">{c.t}</div>
              <div className="text-demon-muted text-sm mt-1">{c.d}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Jak działa + 3 tury */}
      <Section id="jak-dziala">
        <Eyebrow>02 · JAK DZIAŁA · 3 TURY DZIENNIE</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl font-bold mb-8">Trzy tury każdego dnia</h2>
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            { icon: Sunrise, t: "TURA 1", s: "RANO" },
            { icon: Sun, t: "TURA 2", s: "PO POŁUDNIU" },
            { icon: Moon, t: "TURA 3", s: "WIECZOREM" },
          ].map((c) => (
            <div key={c.t} className="glow-card rounded-2xl p-7 text-center">
              <c.icon className="text-violet-400 mx-auto mb-3" size={30} />
              <div className="font-display font-bold text-xl text-glow">{c.t}</div>
              <div className="text-demon-muted text-xs tracking-widest mt-1">{c.s}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Procent składany / Cel */}
      <Section id="cele">
        <Eyebrow>03 · PROCENT SKŁADANY · CEL</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl font-bold mb-8">Cel challenge</h2>
        <div className="max-w-2xl">
          <div className="glow-card rounded-2xl p-8 border-amber-500/30">
            <Target className="text-amber-400 mb-3" size={28} />
            <div className="text-demon-muted text-xs tracking-widest">CEL</div>
            <div className="font-display text-5xl font-extrabold gold-glow text-amber-400 mt-1">$200,000</div>
            <div className="mt-5 space-y-1 font-mono text-sm text-demon-muted">
              <div>Start: <span className="text-white">$350</span> · Czas: <span className="text-white">92 dni</span></div>
              <div>Wymagany średni wzrost: <span className="text-white">7.14% dziennie</span></div>
              <div>Przy 3 turach: <span className="text-white">~2.33% na turę</span></div>
            </div>
          </div>
        </div>
        <p className="text-xs text-demon-muted/80 mt-5">
          Wartości są matematycznym modelem procentu składanego i nie stanowią gwarancji wyniku.
        </p>
      </Section>

      {/* System lotów */}
      <Section id="loty">
        <Eyebrow>04 · SYSTEM LOTÓW</Eyebrow>
        <div className="flex items-center gap-3 mb-6">
          <Calculator className="text-violet-400" size={26} />
          <h2 className="font-display text-3xl sm:text-4xl font-bold">Przelicznik lotów</h2>
        </div>
        <div className="glow-card rounded-2xl overflow-hidden">
          <table className="w-full text-sm font-mono">
            <thead className="bg-violet-950/40 text-demon-muted text-xs tracking-widest">
              <tr>
                <th className="text-left px-5 py-3">LOT</th>
                <th className="text-left px-5 py-3">50 PIPS TP</th>
                <th className="text-left px-5 py-3">50 PIPS SL</th>
              </tr>
            </thead>
            <tbody>
              {LOT_TABLE.map((r) => (
                <tr key={r.lot} className="border-t border-violet-900/20">
                  <td className="px-5 py-2.5 text-white">{r.lot.toFixed(2)}</td>
                  <td className="px-5 py-2.5 text-emerald-400">+{fmtUSD(r.tp, 0)}</td>
                  <td className="px-5 py-2.5 text-red-400">-{fmtUSD(r.sl, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-demon-muted/80 mt-3">Założenie modelu: 0.10 lota = $50 przy ruchu 50 pips.</p>
      </Section>

      {/* Zasady + zarządzanie ryzykiem */}
      <Section id="zasady">
        <Eyebrow>05 · ZASADY · ZARZĄDZANIE RYZYKIEM</Eyebrow>
        <div className="flex items-center gap-3 mb-8">
          <ScrollText className="text-violet-400" size={26} />
          <h2 className="font-display text-3xl sm:text-4xl font-bold">Zasady challenge</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            ["RR", "1 : 1"],
            ["SL", "50 pips"],
            ["TP", "50 pips"],
            ["TURY", "Maks. 3 dziennie"],
            ["START", "$350"],
            ["CZAS", "92 dni"],
            ["CEL", "$200,000"],
          ].map(([k, v]) => (
            <div key={k} className="glow-card rounded-xl p-5 text-center">
              <div className="text-[10px] tracking-widest text-demon-muted">{k}</div>
              <div className="font-mono font-bold text-lg text-white mt-1">{v}</div>
            </div>
          ))}
        </div>
        <p className="text-demon-muted text-sm mt-6 max-w-3xl">
          Nie wymuszaj „odrabiania” straty poprzez zwiększanie lota. Jeśli nie wykonasz wszystkich 3 tur — to nie błąd.
          Dzień może być oznaczony jako <span className="text-amber-400">PARTIAL DAY</span>.
        </p>
      </Section>

      {/* Dołącz / Telegram */}
      <Section id="dolacz" className="text-center">
        <Eyebrow>07 · DOŁĄCZ DO CHALLENGE</Eyebrow>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glow-card rounded-3xl p-10 max-w-2xl mx-auto border-sky-500/30"
        >
          <Send className="text-sky-400 mx-auto mb-4 animate-float" size={40} />
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">Wbij do ekipy THE MONEY CLUB</h2>
          <p className="text-demon-muted mb-7">
            Odezwij się na Telegramie, aby dołączyć do 92-dniowego challenge'u $350 → $200,000.
          </p>
          <a href={telegramUrl} target="_blank" rel="noopener noreferrer" data-testid="join-telegram-button">
            <Button className="h-14 px-10 text-lg font-display font-semibold bg-gradient-to-r from-sky-600 to-cyan-500 border-0 shadow-[0_0_30px_rgba(56,189,248,0.5)]">
              <Send size={20} className="mr-2" /> ODEZWIJ SIĘ NA TELEGRAM
            </Button>
          </a>
        </motion.div>
      </Section>

      {/* Disclaimer + CTA */}
      <Section id="logowanie" className="text-center">
        <Eyebrow>06 · DISCLAIMER · LOGOWANIE</Eyebrow>
        <h2 className="font-display text-3xl sm:text-5xl font-bold mb-5">Wejdź do challenge</h2>
        <p className="text-demon-muted max-w-2xl mx-auto text-sm leading-relaxed mb-8">
          Materiały mają charakter wyłącznie edukacyjny i informacyjny i nie stanowią porady ani rekomendacji
          inwestycyjnej. Trading wiąże się z wysokim ryzykiem utraty kapitału.
        </p>
        <Link to="/login">
          <Button
            data-testid="cta-enter-button"
            className="h-14 px-10 text-lg font-display font-semibold bg-gradient-to-r from-violet-700 to-violet-500 border-0 shadow-[0_0_30px_rgba(168,85,247,0.5)]"
          >
            WEJDŹ DO CHALLENGE <ArrowRight size={20} className="ml-1" />
          </Button>
        </Link>
      </Section>

      <footer className="border-t border-violet-900/30 py-8 text-center text-xs text-demon-muted/70">
        © 2026 THE MONEY CLUB · TRADING ACADEMY ·{" "}
        <Link to="/login" className="text-violet-400 hover:underline">
          Panel uczestnika
        </Link>
      </footer>
    </div>
  );
}
